'use client';

import { useEffect, useRef, useState } from 'react';

// Ported verbatim from helix-web's
// components/blocks/Media/videoCarousel/use-vimeo-player.js — no Product
// dependency to remove here, this hook only ever dealt with the Vimeo SDK.
const useVimeoPlayer = (video) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);

  useEffect(() => {
    if (!iframeRef.current || !video?.video_url) return;

    const loadPlayer = async () => {
      // Lazy-load the Vimeo Player SDK (will not block the video itself by the way)
      const Player = (await import('@vimeo/player')).default;

      // Why not react-player? We have to use the vimeo SDK directly because
      // react-player blinks the video several times when it loads.
      playerRef.current = new Player(iframeRef.current);

      playerRef.current.on('loaded', () => setIsLoaded(true));
      playerRef.current.on('ended', () => setIsPaused(true));
      playerRef.current.on('pause', () => setIsPaused(true));
      playerRef.current.on('play', () => setIsPaused(false));
      playerRef.current.on('volumechange', (e) => {
        setIsMuted(e.volume === 0 || e.muted);
      });
      playerRef.current.on('bufferstart', () => setIsBuffering(true));
      playerRef.current.on('bufferend', () => setIsBuffering(false));

      // Get the initial state of the video (depends on browser policy)
      playerRef.current.getPaused().then((paused) => {
        setIsPaused(paused);
      });
      playerRef.current.getMuted().then((muted) => {
        setIsMuted(muted);
      });
    };

    loadPlayer();

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [video]);

  return {
    iframeRef,
    playerRef,
    isLoaded,
    isMuted,
    isPaused,
    isBuffering,
  };
};

export default useVimeoPlayer;
